// Endpoints RTK Query da feature contacts. Tipos manuais espelhando contrato
// com o back (CRUD + soft-delete LGPD + CSV import stub).
// FIXME: migrar pra paths['/v1/contacts']... quando openapi-typescript regerar.
import { api } from '@/lib/api';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { CursorPage } from '@/features/stores';
import type { RecentConversation } from '@/features/inbox';
import type { CustomerTier } from './utils/tier';

export interface Contact {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  company: string | null;
  /** Loja à qual o contato pertence (null = sem loja / escopo global). */
  storeId: string | null;
  tags: string[];
  notes: string | null;
  avatarTone: AvatarTone;
  ltvCents: number;
  lastContactedAt: string | null;
  /**
   * RF-02: categoria vigente (ano corrente). null = sem categoria — que é o estado
   * da maior parte da base — ou contato anonimizado (o back nunca a devolve).
   */
  tier: CustomerTier | null;
  /** Soft-delete LGPD: timestamp da anonimização (null = contato ativo). */
  anonymizedAt: string | null;
  /** Optimistic locking — readme §19. */
  version: number;
  createdAt: string;
}

/** Rótulo amigável no lugar do PII removido — fonte única em lib/utils/formatters. */
export { ANON_PII_LABEL } from '@/lib/utils/formatters';

/** Contato passou pelo soft-delete LGPD (PII redigido, histórico preservado). */
export function isContactAnonymized(c: Pick<Contact, 'anonymizedAt'>): boolean {
  return Boolean(c.anonymizedAt);
}

export interface CreateContactInput {
  name: string;
  phone: string;
  email?: string;
  company?: string;
  storeId?: string;
  tags?: string[];
  notes?: string;
}

/** Corpo do 409 quando o telefone já existe (back). */
export interface ContactPhoneExistsError {
  code: 'CONTACT_PHONE_EXISTS';
  contactId: string | null;
}

export interface UpdateContactInput {
  id: string;
  version: number;
  name?: string;
  phone?: string;
  email?: string | null;
  company?: string | null;
  tags?: string[];
  notes?: string | null;
  /** Só direciona a invalidação de cache (painel/lista do inbox). NÃO vai no body. */
  conversationId?: string;
}

/**
 * RF-02: lançamento da categoria. `tier: null` REMOVE a categoria do ano.
 *
 * Sem `version` de propósito: a categoria não é campo do Contact, é uma linha
 * própria em (contactId, year) — não há lost-update a proteger, e exigir `version`
 * aqui reproduziria o 409 STALE_ENTITY que já mordeu a tela de CRM.
 */
export interface SetContactTierInput {
  id: string;
  tier: CustomerTier | null;
  /** Ausente = ano corrente (resolvido pelo back). A UI só lança na vigência atual. */
  year?: number;
  /** Só direciona a invalidação de cache (painel/lista do inbox). NÃO vai no body. */
  conversationId?: string;
}

export interface ContactTierResponse {
  contactId: string;
  year: number;
  tier: CustomerTier | null;
  assignedById: string | null;
  updatedAt: string | null;
}

export interface ListContactsArgs {
  q?: string;
  tag?: string[];
  /** GESTOR filtra por loja; 'all' (ou undefined) = todas. Não-GESTOR já vem escopado. */
  storeId?: string;
  cursor?: string;
  /** LGPD: reexibe os contatos excluídos (anonimizados), escondidos por padrão. */
  includeAnonymized?: boolean;
}

export interface ImportCsvResponse {
  jobId: string;
  enqueuedAt: string;
}

function qs(params: Record<string, string | string[] | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) parts.push(`${k}=${encodeURIComponent(item)}`);
    } else {
      parts.push(`${k}=${encodeURIComponent(v)}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export const contactsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listContacts: build.query<CursorPage<Contact>, ListContactsArgs | void>({
      query: (args) =>
        `/contacts${qs({
          q: args?.q,
          tag: args?.tag,
          storeId: args?.storeId,
          cursor: args?.cursor,
          // Só vai na URL quando ligado — o back trata a ausência como false.
          includeAnonymized: args?.includeAnonymized ? 'true' : undefined,
        })}`,
      // Back devolve { data, pageInfo: { nextCursor, hasMore } } (envelope canônico
      // do readme §12). Front consome { items, nextCursor } — sem isso a lista vira
      // undefined silenciosamente e o merge da paginação quebra.
      transformResponse: (raw: {
        data: Contact[];
        pageInfo: { nextCursor: string | null; hasMore: boolean };
      }): CursorPage<Contact> => ({
        items: raw.data,
        nextCursor: raw.pageInfo.nextCursor,
      }),
      // Faz parte da cache key: sem isso, ligar "Mostrar excluídos" reusaria a lista
      // filtrada em cache e o toggle não teria efeito visível.
      serializeQueryArgs: ({ queryArgs }) => ({
        q: queryArgs?.q,
        tag: queryArgs?.tag,
        storeId: queryArgs?.storeId,
        includeAnonymized: queryArgs?.includeAnonymized,
      }),
      merge: (current, incoming, { arg }) => {
        if (!arg?.cursor) return incoming;
        const ids = new Set(current.items.map((c) => c.id));
        return {
          items: [...current.items, ...incoming.items.filter((c) => !ids.has(c.id))],
          nextCursor: incoming.nextCursor,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: (result) =>
        result
          ? [
              { type: 'Contact' as const, id: 'LIST' },
              ...result.items.map((c) => ({ type: 'Contact' as const, id: c.id })),
            ]
          : [{ type: 'Contact' as const, id: 'LIST' }],
    }),
    getContact: build.query<Contact, string>({
      query: (id) => `/contacts/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Contact' as const, id }],
    }),
    // Histórico de atendimento do contato — conversas (sessões) anteriores.
    // Reusa o tipo RecentConversation do inbox (mesmo card + modal read-only).
    listContactConversations: build.query<RecentConversation[], string>({
      query: (contactId) => `/conversations/by-contact/${contactId}`,
      providesTags: (_r, _e, contactId) => [
        { type: 'Conversation' as const, id: `CONTACT_${contactId}` },
      ],
    }),
    getContactTags: build.query<string[], void>({
      query: () => '/contacts/tags',
      providesTags: [{ type: 'Contact' as const, id: 'TAGS' }],
    }),
    createContact: build.mutation<Contact, CreateContactInput>({
      query: (body) => ({ url: '/contacts', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Contact' as const, id: 'LIST' },
        { type: 'Contact' as const, id: 'TAGS' },
      ],
    }),
    updateContact: build.mutation<Contact, UpdateContactInput>({
      // conversationId é só pra invalidação — fica de fora do body do PATCH.
      query: ({ id, conversationId: _conversationId, ...body }) => ({
        url: `/contacts/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Contact' as const, id: arg.id },
        { type: 'Contact' as const, id: 'LIST' },
        // Tag nova passa a aparecer no autocomplete (/contacts/tags).
        { type: 'Contact' as const, id: 'TAGS' },
        // Editado pelo painel do inbox: refresca a conversa e a lista (mostram tags).
        ...(arg.conversationId
          ? [
              { type: 'Conversation' as const, id: arg.conversationId },
              { type: 'Conversation' as const, id: 'LIST' },
            ]
          : []),
      ],
    }),
    setContactTier: build.mutation<ContactTierResponse, SetContactTierInput>({
      // conversationId é só pra invalidação — fica de fora do body do PUT.
      query: ({ id, conversationId: _conversationId, ...body }) => ({
        url: `/contacts/${id}/tier`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Contact' as const, id: arg.id },
        // A lista de Clientes mostra o badge da categoria.
        { type: 'Contact' as const, id: 'LIST' },
        // Lançado pelo painel do inbox: a conversa carrega o tier no ContactSummary.
        ...(arg.conversationId
          ? [
              { type: 'Conversation' as const, id: arg.conversationId },
              { type: 'Conversation' as const, id: 'LIST' },
            ]
          : []),
      ],
    }),
    deleteContact: build.mutation<{ ok: true }, string>({
      // Soft-delete: back anonimiza dados PII (LGPD §22 readme).
      query: (id) => ({ url: `/contacts/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Contact' as const, id },
        { type: 'Contact' as const, id: 'LIST' },
      ],
    }),
    importContactsCsv: build.mutation<ImportCsvResponse, FormData>({
      query: (formData) => ({
        url: '/contacts/import-csv',
        method: 'POST',
        body: formData,
        // FormData: baseQuery detecta e deixa o browser setar o Content-Type
        // com o boundary correto. Não passamos header manual aqui.
      }),
      invalidatesTags: [{ type: 'Contact' as const, id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListContactsQuery,
  useGetContactQuery,
  useListContactConversationsQuery,
  useGetContactTagsQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useSetContactTierMutation,
  useDeleteContactMutation,
  useImportContactsCsvMutation,
} = contactsApi;
